/**
 * Register — Login ile aynı layout: yatayda [Saat+QR | Form], dikeyde [Saat+QR üst, Form alt]
 * QR: Telefondan okut → telefonda kayıt ol
 */
import { Bot, Eye, EyeOff } from 'lucide-react'
import { QRCodeSVG } from 'qrcode.react'
import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import CaptchaChallenge from '../components/shared/CaptchaChallenge'
import { useAuth } from '../contexts/AuthContext'

export default function Register() {
  const [step, setStep] = useState(1)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [pw, setPw] = useState('')
  const [pw2, setPw2] = useState('')
  const [err, setErr] = useState('')
  const [loading, setLoading] = useState(false)
  const [showPw, setShowPw] = useState(false)
  const [captchaOk, setCaptchaOk] = useState(false)
  const [time, setTime] = useState(new Date())
  const [lang, setLang] = useState(() => {
    const l = navigator.language?.split('-')[0] ?? 'en'
    return ['tr','en','de','fr','es','ar'].includes(l) ? l : 'en'
  })
  const { register } = useAuth()
  const navigate = useNavigate()

  useEffect(() => { const t = setInterval(() => setTime(new Date()), 30000); return () => clearInterval(t) }, [])

  const next = () => {
    if (!name.trim() || !email.trim()) { setErr('All fields are required'); return }
    if (!email.includes('@')) { setErr('Enter a valid email'); return }
    setErr(''); setStep(2)
  }

  const submit = async () => {
    if (!pw || !pw2) { setErr('Password fields are required'); return }
    if (pw !== pw2) { setErr('Passwords do not match'); return }
    if (pw.length < 8) { setErr('Password must be at least 8 characters'); return }
    setErr(''); setLoading(true)
    try {
      await register(email, pw, name, lang)
      setStep(3)
      setTimeout(() => navigate('/login', { replace: true }), 3000)
    } catch (e: any) { setErr(e.message || 'Registration failed') }
    finally { setLoading(false) }
  }

  const qrUrl = `${window.location.origin}/register`

  return (
    <div className="au">
      <style>{CSS}</style>
      <div className="au-bg" />

      <div className="au-grid">
        {/* Sol: Saat + QR */}
        <div className="au-side">
          <div className="au-clock">
            <div className="au-time">{time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</div>
            <div className="au-date">{time.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}</div>
          </div>
          <div className="au-qr">
            <div className="au-qr-box">
              <QRCodeSVG value={qrUrl} size={120} bgColor="transparent" fgColor="rgba(255,255,255,0.75)" level="M" />
            </div>
            <div className="au-qr-label">Scan with phone to register on mobile</div>
          </div>
        </div>

        {/* Sağ: Form */}
        <div className="au-main">
          <div className="au-avatar">{step === 3 ? '✓' : <Bot size={28} color="#818cf8" />}</div>
          <div className="au-title">{step === 3 ? 'Welcome!' : 'Create Account'}</div>
          {step < 3 && <div className="au-step">Step {step} / 2</div>}

          <div className="au-form">
            {err && <div className="au-err">{err}</div>}
            {step === 1 && (
              <>
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Full Name" required autoFocus className="au-inp" />
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" required className="au-inp" />
                <select value={lang} onChange={e => setLang(e.target.value)} className="au-inp" style={{ cursor: 'pointer' }}>
                  <option value="tr">🇹🇷 Türkçe</option>
                  <option value="en">🇬🇧 English</option>
                  <option value="de">🇩🇪 Deutsch</option>
                  <option value="fr">🇫🇷 Français</option>
                  <option value="es">🇪🇸 Español</option>
                  <option value="ar">🇸🇦 العربية</option>
                </select>
                <button onClick={next} className="au-btn">Continue →</button>
              </>
            )}
            {step === 2 && (
              <>
                <div className="au-pw">
                  <input type={showPw ? 'text' : 'password'} value={pw} onChange={e => setPw(e.target.value)} placeholder="Password (min 8)" required autoFocus className="au-inp au-inp--pw" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="au-pw-btn">{showPw ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                </div>
                <input type="password" value={pw2} onChange={e => setPw2(e.target.value)} placeholder="Confirm password" required className="au-inp" />
                <CaptchaChallenge onVerified={setCaptchaOk} />
                <div className="au-row">
                  <button onClick={() => { setStep(1); setErr('') }} className="au-btn au-btn--sec">← Back</button>
                  <button onClick={() => { void submit() }} disabled={loading || !captchaOk} className="au-btn au-btn--main">{loading ? '⏳' : 'Sign Up'}</button>
                </div>
              </>
            )}
            {step === 3 && (
              <div style={{ textAlign: 'center' }}>
                <p className="au-ok">Verification email sent to <span style={{ color: '#10b981', fontWeight: 600 }}>{email}</span></p>
                <Link to="/login" className="au-btn" style={{ display: 'inline-block', width: 'auto', padding: '0.7em 1.8em', textDecoration: 'none' }}>Sign In →</Link>
              </div>
            )}
          </div>
          <Link to="/login" className="au-link">Sign In</Link>
        </div>
      </div>
    </div>
  )
}

const CSS = `
.au{width:100vw;height:100vh;height:100dvh;display:flex;align-items:center;justify-content:center;background:#000;position:relative;overflow:hidden;font-family:-apple-system,BlinkMacSystemFont,"SF Pro Display",sans-serif}
.au-bg{position:absolute;inset:0;background-image:url('/wallpaper-night.jpg');background-size:cover;background-position:center;filter:brightness(0.55) saturate(1.2)}
.au-bg::after{content:'';position:absolute;inset:0;background:linear-gradient(160deg,rgba(12,10,29,0.6) 0%,rgba(26,14,58,0.5) 25%,rgba(45,22,84,0.4) 45%,rgba(26,39,68,0.5) 65%,rgba(12,25,41,0.6) 85%,rgba(6,13,24,0.7) 100%);backdrop-filter:blur(2px)}
.au-grid{z-index:2;display:flex;align-items:center;justify-content:center;gap:clamp(2rem,6vmin,5rem);width:100%;height:100%;padding:4vmin;box-sizing:border-box}
.au-side{display:flex;flex-direction:column;align-items:center;gap:clamp(1rem,3vmin,2rem);flex-shrink:0}
.au-clock{text-align:center}
.au-time{font-size:clamp(2.5rem,7vmin,6rem);font-weight:700;color:#fff;letter-spacing:-0.04em;line-height:1;text-shadow:0 2px 16px rgba(0,0,0,0.3)}
.au-date{font-size:clamp(0.8rem,1.8vmin,1.2rem);color:rgba(255,255,255,0.65);margin-top:0.3em}
.au-qr{display:flex;flex-direction:column;align-items:center}
.au-qr-box{padding:clamp(8px,1.5vmin,16px);background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.08);border-radius:clamp(10px,1.5vmin,16px)}
.au-qr-box svg{width:clamp(80px,12vmin,140px)!important;height:clamp(80px,12vmin,140px)!important}
.au-qr-label{font-size:clamp(0.6rem,1.2vmin,0.8rem);color:rgba(255,255,255,0.35);margin-top:0.5em;text-align:center}
.au-main{display:flex;flex-direction:column;align-items:center;width:min(380px,90vw);flex-shrink:0}
.au-avatar{width:clamp(3rem,6vmin,4.5rem);height:clamp(3rem,6vmin,4.5rem);border-radius:50%;background:linear-gradient(145deg,rgba(255,255,255,0.15),rgba(255,255,255,0.05));backdrop-filter:blur(20px);border:2px solid rgba(255,255,255,0.15);display:flex;align-items:center;justify-content:center;font-size:clamp(1.4rem,3vmin,2.2rem);box-shadow:0 4px 16px rgba(0,0,0,0.4)}
.au-title{font-size:clamp(1rem,2.2vmin,1.3rem);font-weight:600;color:#fff;margin:0.5em 0 0.3em}
.au-step{font-size:clamp(0.7rem,1.2vmin,0.85rem);color:rgba(255,255,255,0.4);margin-bottom:0.8em}
.au-form{width:100%}
.au-inp{width:100%;padding:14px 16px;margin-bottom:10px;background:rgba(255,255,255,0.08);border:1px solid rgba(255,255,255,0.12);border-radius:10px;color:#fff;font-size:16px;outline:none;box-sizing:border-box;font-family:inherit;backdrop-filter:blur(10px);transition:border-color 0.2s}
.au-inp::placeholder{color:rgba(255,255,255,0.35);font-size:15px}
.au-inp:focus{border-color:rgba(129,140,248,0.6);background:rgba(255,255,255,0.1)}
.au-inp--pw{padding-right:2.8rem}
.au-pw{position:relative;width:100%}
.au-pw-btn{position:absolute;right:0.7rem;top:50%;transform:translateY(-50%);background:none;border:none;color:rgba(255,255,255,0.35);cursor:pointer;padding:4px}
.au-err{padding:10px 14px;margin-bottom:10px;border-radius:8px;background:rgba(239,68,68,0.12);border:1px solid rgba(239,68,68,0.25);color:#fca5a5;font-size:13px;text-align:center}
.au-btn{width:100%;padding:14px;border:none;border-radius:10px;background:rgba(255,255,255,0.12);color:#fff;font-size:16px;font-weight:600;cursor:pointer;font-family:inherit;backdrop-filter:blur(10px);box-shadow:0 2px 6px rgba(0,0,0,0.2),inset 0 1px 0 rgba(255,255,255,0.08);transition:all 0.2s;text-align:center}
.au-btn:hover{background:rgba(255,255,255,0.18)}
.au-btn:disabled{opacity:0.5;cursor:wait}
.au-btn--sec{background:transparent;border:1px solid rgba(255,255,255,0.1);box-shadow:none}
.au-btn--main{flex:2}
.au-row{display:flex;gap:clamp(5px,0.7vmin,8px)}
.au-row .au-btn{flex:1}
.au-ok{font-size:14px;color:rgba(255,255,255,0.6);margin-bottom:1em;line-height:1.5}
.au-link{margin-top:0.8em;color:rgba(255,255,255,0.35);font-size:13px;text-decoration:none;transition:color 0.2s}
.au-link:hover{color:rgba(255,255,255,0.7)}
@media(orientation:portrait){
  .au-grid{flex-direction:column;gap:2vmin}
  .au-side{flex-direction:row;gap:clamp(1rem,4vw,2rem)}
  .au-qr-box svg{width:clamp(60px,15vw,100px)!important;height:clamp(60px,15vw,100px)!important}
}
@media(max-height:400px){
  .au-side{display:none}
  .au-avatar{display:none}
  .au-title{display:none}
  .au-step{display:none}
}
`
